import { create } from 'zustand'
import type { ScanResult, ScanProgress, ScanFilters, SortOptions } from '@/types/scan'

interface ScanState {
  scans: ScanResult[]
  currentScan: ScanResult | null
  scanProgress: Record<string, ScanProgress>
  isLoading: boolean
  error: string | null
  filters: ScanFilters
  sort: SortOptions
  
  // Actions
  setScans: (scans: ScanResult[]) => void
  addScan: (scan: ScanResult) => void
  updateScan: (scanId: string, updates: Partial<ScanResult>) => void
  setCurrentScan: (scan: ScanResult | null) => void
  setScanProgress: (scanId: string, progress: ScanProgress) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  setFilters: (filters: Partial<ScanFilters>) => void
  setSort: (sort: SortOptions) => void
  clearScans: () => void
  removeScan: (scanId: string) => void
}

export const useScanStore = create<ScanState>((set) => ({
  scans: [],
  currentScan: null,
  scanProgress: {},
  isLoading: false,
  error: null,
  filters: {},
  sort: { field: 'created_at', direction: 'desc' },

  setScans: (scans: ScanResult[]) => {
    set({ scans, error: null })
  },

  addScan: (scan: ScanResult) => {
    set((state) => ({
      scans: [scan, ...state.scans],
      error: null,
    }))
  },

  updateScan: (scanId: string, updates: Partial<ScanResult>) => {
    set((state) => ({
      scans: state.scans.map((scan) =>
        scan.id === scanId ? { ...scan, ...updates } : scan
      ),
      currentScan: state.currentScan?.id === scanId 
        ? { ...state.currentScan, ...updates }
        : state.currentScan,
      error: null,
    }))
  },

  setCurrentScan: (scan: ScanResult | null) => {
    set({ currentScan: scan, error: null })
  },

  setScanProgress: (scanId: string, progress: ScanProgress) => {
    set((state) => ({
      scanProgress: {
        ...state.scanProgress,
        [scanId]: progress,
      },
    }))
  },

  setLoading: (loading: boolean) => {
    set({ isLoading: loading })
  },

  setError: (error: string | null) => {
    set({ error })
  },

  setFilters: (filters: Partial<ScanFilters>) => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
    }))
  },

  setSort: (sort: SortOptions) => {
    set({ sort })
  },

  clearScans: () => {
    set({
      scans: [],
      currentScan: null,
      scanProgress: {},
      error: null,
    })
  },

  removeScan: (scanId: string) => {
    set((state) => ({
      scans: state.scans.filter((scan) => scan.id !== scanId),
      currentScan: state.currentScan?.id === scanId ? null : state.currentScan,
      scanProgress: Object.fromEntries(
        Object.entries(state.scanProgress).filter(([id]) => id !== scanId)
      ),
      error: null,
    }))
  },
}))